import React, { useEffect, useMemo, useRef, useState } from "react";
import { Eye, Send } from "lucide-react";
import {
  getAllInvoices,
  updateOrderTracking,
  getAllDeliveryBoys,
  assignDeliveryBoy,
  updateAdminStatusForDelivery,
  updatePriceVisibility,
} from "../../services/orderList/order.service";
import { toast } from "react-toastify";

type AdminStatus = "Come to Office" | "Go for Delivery" | "Rejected";
type AdminStatusState = AdminStatus | "";

// const BASE_URL = "https://api.sonachandiwale.com";
const statusOptions = ["Pending", "In Progress", "On The Way", "Delivered"];
const adminStatusOptions: AdminStatus[] = ["Come to Office", "Go for Delivery", "Rejected"];

const formatNumberIN = (value: number): string => {
  const safe = Number.isFinite(value) ? value : 0;
  return new Intl.NumberFormat("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(safe);
};

const getFullName = (user: any): string => {
  if (!user) return "N/A";
  const name = `${user.firstName || ""} ${user.lastName || ""}`.trim();
  return name || "N/A";
};

const inputStyle: React.CSSProperties = {
  width: "180px",
  height: "38px",
  padding: "0 10px",
  borderRadius: "6px",
  border: "1px solid #ccc",
};
const thStyle: React.CSSProperties = { padding: "12px", textAlign: "left" };
const tdStyle: React.CSSProperties = { padding: "10px" };
const modalOverlayStyle: React.CSSProperties = {
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: "rgba(0,0,0,0.5)",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  zIndex: 1000,
};
const primaryButtonStyle: React.CSSProperties = {
  background: "#2196F3",
  color: "#fff",
  padding: "8px 12px",
  border: "none",
  borderRadius: "6px",
  cursor: "pointer",
};
const secondaryButtonStyle: React.CSSProperties = {
  background: "#777",
  color: "#fff",
  padding: "8px 12px",
  border: "none",
  borderRadius: "6px",
  cursor: "pointer",
};
const viewButtonStyle: React.CSSProperties = {
  background: "#2196F3",
  border: "none",
  cursor: "pointer",
  padding: "6px 12px",
  borderRadius: "4px",
  color: "white",
  display: "inline-flex",
  alignItems: "center",
  gap: "5px",
};
const assignButtonStyle: React.CSSProperties = {
  color: "#fff",
  border: "none",
  padding: "6px 12px",
  borderRadius: "4px",
  display: "inline-flex",
  alignItems: "center",
  gap: "5px",
};
const paginationButtonStyle = (disabled: boolean): React.CSSProperties => ({
  padding: "8px 16px",
  cursor: disabled ? "not-allowed" : "pointer",
  backgroundColor: disabled ? "#ccc" : "#2196F3",
  color: "white",
  border: "none",
  borderRadius: "4px",
});
const getOrderStatusStyle = (status: string): React.CSSProperties => {
  let color = "gray";
  if (status === "Delivered") color = "green";
  else if (status === "On The Way") color = "orange";
  else if (status === "In Progress") color = "blue";
  return { color, fontWeight: 500 };
};
const getAdminStatusStyle = (status: string): React.CSSProperties => {
  let color = "gray";
  if (status === "Go for Delivery") color = "orange";
  else if (status === "Come to Office") color = "blue";
  else if (status === "Rejected") color = "red";
  return { color, fontWeight: 500 };
};

const DUMMY_IMAGE =
  "data:image/svg+xml;utf8," +
  encodeURIComponent(`
    <svg xmlns="http://www.w3.org/2000/svg" width="80" height="80">
      <rect width="100%" height="100%" fill="#f3f4f6"/>
      <text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle"
        font-family="Arial" font-size="11" fill="#9ca3af">No Image</text>
    </svg>
  `);

const OrderList: React.FC = () => {
  const [orders, setOrders] = useState<any[]>([]);
  const [deliveryBoys, setDeliveryBoys] = useState<any[]>([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState("");
  const [selectedArticleName, setSelectedArticleName] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedMetal, setSelectedMetal] = useState("");
  const [adminStatusFilter, setAdminStatusFilter] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<any>(null);
  const [selectedStatus, setSelectedStatus] = useState("Pending");
  const [adminStatus, setAdminStatus] = useState<AdminStatusState>("");
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assignOrder, setAssignOrder] = useState<any>(null);
  const [selectedDeliveryBoy, setSelectedDeliveryBoy] = useState("");
  const hasFetchedRef = useRef(false);
  const [paymentStatusFilter, setPaymentStatusFilter] = useState("");
  const paymentStatusOptions = ["Pending", "Processed"];

  const userData = localStorage.getItem("user");
  const currentUserRoleId = userData ? JSON.parse(userData)?.role_id : null;
  const isSuperAdmin = currentUserRoleId === "1";
  const itemsPerPage = 10;

  const fetchOrders = async (showFullLoader = false) => {
    try {
      if (showFullLoader) setInitialLoading(true);
      else setRefreshing(true);
      const res = await getAllInvoices();
      setOrders(res?.data?.data || []);
    } catch (error) {
      console.error("Error fetching orders:", error);
      toast.error("Failed to fetch orders");
    } finally {
      setInitialLoading(false);
      setRefreshing(false);
    }
  };

  const fetchDeliveryBoys = async () => {
    try {
      const res = await getAllDeliveryBoys();
      setDeliveryBoys(res?.data?.data || []);
    } catch (error) {
      console.error("Error fetching delivery boys:", error);
      toast.error("Failed to fetch delivery boys");
    }
  };

  useEffect(() => {
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;
    fetchOrders(true);
    fetchDeliveryBoys();
  }, []);

  const openStatusModal = (order: any) => {
    setSelectedOrder(order);
    setSelectedStatus(order?.orderStatus || "Pending");
    setAdminStatus((order?.deliverySession?.adminStatus || "") as AdminStatusState);
    setShowModal(true);
  };

  const articleNames = useMemo(
    () => Array.from(new Set(orders.map((item) => item?.item?.articleName?.trim()).filter(Boolean))),
    [orders]
  );
  const categoryList = useMemo(
    () =>
      Array.from(
        new Map(
          orders
            .filter((item) => item?.category)
            .map((item) => [item.category.id, { ...item.category, name: item.category.name?.trim() }])
        ).values()
      ),
    [orders]
  );
  const metalTypeList = useMemo(
    () => Array.from(new Set(orders.map((item) => item?.metalType?.name).filter(Boolean))),
    [orders]
  );

  const filteredOrders = useMemo(() => {
    return orders.filter((item) => {
      const matchesSearch =
        !search || String(item?.orderId || "").toLowerCase().includes(search.toLowerCase());
      const matchesArticle =
        !selectedArticleName || item?.item?.articleName?.trim() === selectedArticleName;
      const matchesCategory =
        !selectedCategory || item?.category?.name?.trim() === selectedCategory;
      const matchesMetal = !selectedMetal || item?.metalType?.name === selectedMetal;
      const adminValue = item?.deliverySession?.adminStatus || "Pending";
      const matchesAdminStatus = !adminStatusFilter || adminValue === adminStatusFilter;
      const paymentStatus = item?.paymentStatus || "Pending";
      const matchesPaymentStatus = !paymentStatusFilter || paymentStatus === paymentStatusFilter;

      return matchesSearch && matchesArticle && matchesCategory && matchesMetal && matchesAdminStatus && matchesPaymentStatus;
    });
  }, [orders, search, selectedArticleName, selectedCategory, selectedMetal, adminStatusFilter, paymentStatusFilter]);

  const totalPages = Math.ceil(filteredOrders.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedData = filteredOrders.slice(startIndex, startIndex + itemsPerPage).map((item, idx) => ({
    ...item,
    serial: startIndex + idx + 1,
  }));

  const resetAllFilters = () => {
    setSearch("");
    setSelectedArticleName("");
    setSelectedCategory("");
    setSelectedMetal("");
    setPaymentStatusFilter("");
    setAdminStatusFilter("");
    setCurrentPage(1);
    toast.info("All filters cleared");
  };

  const updateOrderStatus = async () => {
    if (!selectedOrder?.orderId) return;
    try {
      await updateOrderTracking({ orderId: selectedOrder.orderId, orderStatus: selectedStatus });
      setOrders((prev) =>
        prev.map((order) =>
          order.orderId === selectedOrder.orderId
            ? { ...order, orderStatus: selectedStatus, deliverySession: { ...order.deliverySession, adminStatus } }
            : order
        )
      );
      setSelectedOrder((prev: any) => ({
        ...prev,
        orderStatus: selectedStatus,
        deliverySession: { ...prev?.deliverySession, adminStatus },
      }));
      toast.success(`Order status updated to ${selectedStatus}`);
      setShowModal(false);
    } catch (error) {
      console.error("Status update error:", error);
      toast.error("Failed to update order status");
    }
  };

  const handleAssignDeliveryBoy = async () => {
    if (!assignOrder?.orderId) {
      toast.warning("Order not selected");
      return;
    }
    if (!selectedDeliveryBoy) {
      toast.warning("Please select delivery boy");
      return;
    }
    if (assignOrder?.deliveryBoyId) {
      toast.warning("Delivery boy is already assigned to this order!");
      setShowAssignModal(false);
      return;
    }
    try {
      await assignDeliveryBoy(assignOrder.orderId, { deliveryBoyId: selectedDeliveryBoy });
      toast.success("Delivery boy assigned successfully");
      setShowAssignModal(false);
      setSelectedDeliveryBoy("");
      setAssignOrder(null);
      fetchOrders(false);
    } catch (error) {
      console.error("Assign error:", error);
      toast.error("Failed to assign delivery boy");
    }
  };

  // const getImageUrl = (item: any) => {
  //   const image = item?.item?.image;
  //   if (Array.isArray(image) && image.length > 0 && image[0]) return `${BASE_URL}${image[0]}`;
  //   if (typeof image === "string" && image.trim()) return `${BASE_URL}${image}`;
  //   return DUMMY_IMAGE;
  // };

  const getImageUrl = (item: any) => {
    const image = item?.item?.image;

    if (!image) return DUMMY_IMAGE;

    if (Array.isArray(image)) {
      if (image.length === 0) return DUMMY_IMAGE;
      const firstImage = image[0];
      if (!firstImage) return DUMMY_IMAGE;
      // Already full URL from backend - return as is
      return firstImage;
    }

    if (typeof image === 'string' && image.trim()) {
      // Already full URL from backend - return as is
      return image;
    }

    return DUMMY_IMAGE;
  };
  const isPickupImageAvailable = Boolean(selectedOrder?.deliverySession?.pickupImage);
  const isAlreadyOutForDelivery =
    selectedOrder?.deliverySession?.adminStatus === "Go for Delivery" ||
    selectedOrder?.deliverySession?.adminStatus === "Rejected";
  const isDeliveryUpdateDisabled = !adminStatus || !isPickupImageAvailable || isAlreadyOutForDelivery;

  // Safe numeric conversions for modal calculations
  const sellerPurity = Number(selectedOrder?.sellerPurity) || 0;
  const buyerPurity = Number(selectedOrder?.buyerPurity) || 0;
  const quantity = Number(selectedOrder?.quantity) || 0;
  const perGmPrice = Number(selectedOrder?.perGmPrice) || 0;
  const totalAdminProfit = Number(selectedOrder?.totalAdminProfit) || 0;
  const marginWastageProfit = Number(selectedOrder?.marginWastageProfit) || 0;
  const wastageMakingChargesPerGram = Number(selectedOrder?.WastageMakingChargesPerGram) || 0;
  const makingChargesPerGram = Number(selectedOrder?.makingChargesPerGram) || 0;
  const gstPercent = Number(selectedOrder?.gstPercent) || 0;
  const sellerTotalAmount = Number(selectedOrder?.sellertotalAmount) || 0;
  const buyerTotalAmount = Number(selectedOrder?.buyertotalAmount) || 0;
  const buyerTotalAmountGst = Number(selectedOrder?.buyertotalAmmoumtGst) || 0;

  // Discount variables
  const discountAmount = Number(selectedOrder?.discountAmount) || 0;
  const discountAfterAmount = Number(selectedOrder?.discountAfterAmount) || 0;
  const discountAfterAmountWithGst = Number(selectedOrder?.discountAfterAmountWithGst) || 0;

  const sellerFineGm = (sellerPurity * quantity) / 100;
  const buyerFineGm = (buyerPurity * quantity) / 100;
  const sellerPriceWithoutMaking = sellerFineGm * perGmPrice;
  const buyerPriceWithoutMaking = buyerFineGm * perGmPrice;
  const adminFineGm = perGmPrice > 0 ? totalAdminProfit / perGmPrice : 0;
  const marginMakingChargesCorrected = wastageMakingChargesPerGram * quantity;

  if (initialLoading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh", fontSize: "18px", color: "#666" }}>
        Loading orders...
      </div>
    );
  }

  return (
    <div className="employee-container">
      <style>
        {`
          .sticky-table-container {
            max-height: calc(100vh - 220px);
            overflow-y: auto;
            overflow-x: auto;
            position: relative;
          }
          .sticky-table-container table {
            width: 100%;
            border-collapse: collapse;
          }
          .sticky-table-container thead {
            position: sticky;
            top: 0;
            background-color: #f5f5f5 !important;
            z-index: 10;
          }
          .sticky-table-container thead th {
            background-color: #0e1a31 !important;
            box-shadow: inset 0 -1px 0 #ddd;
          }
        `}
      </style>

      <div className="header-bar">
        <div style={{ display: "flex", gap: "10px", alignItems: "center", flexWrap: "wrap", marginBottom: "15px" }}>
          <input
            type="text"
            placeholder="Search by Order ID..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setCurrentPage(1);
            }}
            style={{ ...inputStyle, width: "220px" }}
          />
          {articleNames.length > 0 && (
            <select
              value={selectedArticleName}
              onChange={(e) => {
                setSelectedArticleName(e.target.value);
                setCurrentPage(1);
              }}
              style={inputStyle}
            >
              <option value="">All Articles</option>
              {articleNames.map((name, i) => (
                <option key={i} value={name}>
                  {name}
                </option>
              ))}
            </select>
          )}
          {categoryList.length > 0 && (
            <select
              value={selectedCategory}
              onChange={(e) => {
                setSelectedCategory(e.target.value);
                setCurrentPage(1);
              }}
              style={inputStyle}
            >
              <option value="">All Categories</option>
              {categoryList.map((cat) => (
                <option key={cat.id} value={cat.name}>
                  {cat.name}
                </option>
              ))}
            </select>
          )}
          {metalTypeList.length > 0 && (
            <select
              value={selectedMetal}
              onChange={(e) => {
                setSelectedMetal(e.target.value);
                setCurrentPage(1);
              }}
              style={inputStyle}
            >
              <option value="">All Metal Types</option>
              {metalTypeList.map((metal, i) => (
                <option key={i} value={metal}>
                  {metal}
                </option>
              ))}
            </select>
          )}

          <select
            value={paymentStatusFilter}
            onChange={(e) => {
              setPaymentStatusFilter(e.target.value);
              setCurrentPage(1);
            }}
            style={inputStyle}
          >
            <option value="">All Payment Status</option>
            {paymentStatusOptions.map((status) => (
              <option key={status} value={status}>
                {status}
              </option>
            ))}
          </select>
          <select
            value={adminStatusFilter}
            onChange={(e) => {
              setAdminStatusFilter(e.target.value);
              setCurrentPage(1);
            }}
            style={inputStyle}
          >
            <option value="">All Admin Status</option>
            <option value="Pending">Pending</option>
            {adminStatusOptions.map((status) => (
              <option key={status} value={status}>
                {status}
              </option>
            ))}
          </select>
          {(search || selectedArticleName || selectedCategory || selectedMetal || adminStatusFilter || paymentStatusFilter) && (
            <button
              onClick={resetAllFilters}
              style={{
                padding: "0 15px",
                height: "38px",
                borderRadius: "6px",
                border: "1px solid #dc3545",
                backgroundColor: "#dc3545",
                color: "white",
                cursor: "pointer",
              }}
            >
              Clear All Filters
            </button>
          )}
          {refreshing && <span style={{ fontSize: "13px", color: "#777" }}>Refreshing...</span>}
        </div>
      </div>

      <div style={{ marginBottom: "10px", fontSize: "14px", color: "#666", marginTop: "10px" }}>
        Showing {paginatedData.length} of {filteredOrders.length} orders (Total: {orders.length})
      </div>

      <div className="sticky-table-container">
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ backgroundColor: "#f5f5f5", borderBottom: "2px solid #ddd" }}>
              {[
                "Image",
                "Order ID",
                "Buyer",
                "Buyer Mobile",
                "Category",
                "Article",
                "Metal",
                "Quantity",
                "UOM",
                "Seller",
                "Seller Business",
                "Seller Mobile",
                "Payment Method",
                "Payment Status",
                "Delivery Boy",
                "Wastage %",
                "Making/gm",
                "Metal Silver Pay (In gram)",
                "Amount",
                "Amount + GST",
                "Discount Amount",
                "Final Discount Amount",
                "Discount Amount + GST",
                "Status",
                "Admin Status",
                "Action",
                "Assign",
              ].map((head) => (
                <th key={head} style={thStyle}>
                  {head}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {paginatedData.length > 0 ? (
              paginatedData.map((item) => {
                const wastagePercent = Number(item.wastagePercent) || 0;
                const commissionPercent = Number(item.commissionPercent) || 0;
                const makingChargesPerGram = Number(item.makingChargesPerGram) || 0;
                const wastageMakingChargesPerGram = Number(item.WastageMakingChargesPerGram) || 0;
                const buyertotalAmount = Number(item.buyertotalAmount) || 0;
                const buyertotalAmmoumtGst = Number(item.buyertotalAmmoumtGst) || 0;
                const discountAmount = Number(item.discountAmount) || 0;
                const discountAfterAmount = Number(item.discountAfterAmount) || 0;
                const discountAfterAmountWithGst = Number(item.discountAfterAmountWithGst) || 0;

                return (
                  <tr key={item.id || item.orderId} style={{ borderBottom: "1px solid #eee" }}>
                    <td style={tdStyle}>
                      <img
                        src={getImageUrl(item)}
                        alt="article"
                        onError={(e) => {
                          e.currentTarget.onerror = null;
                          e.currentTarget.src = DUMMY_IMAGE;
                        }}
                        style={{ width: "50px", height: "50px", objectFit: "cover", borderRadius: "6px", backgroundColor: "#f3f4f6" }}
                      />
                    </td>
                    <td style={tdStyle}>{item.orderId || "-"}</td>
                    <td style={tdStyle}>{getFullName(item.buyer)}</td>
                    <td style={tdStyle}>{item.buyer?.mobileNumber || "N/A"}</td>
                    <td style={tdStyle}>{item.category?.name || "-"}</td>
                    <td style={tdStyle}>{item.item?.articleName || "-"}</td>
                    <td style={tdStyle}>{item.metalType?.name || "-"}</td>
                    <td style={tdStyle}>{item.quantity || "-"}</td>
                    <td style={tdStyle}>{item.uom?.name || ""}</td>
                    <td style={tdStyle}>{getFullName(item.seller)}</td>
                    <td style={tdStyle}>{item.seller?.businessName || "N/A"}</td>
                    <td style={tdStyle}>{item.seller?.mobileNumber || "N/A"}</td>
                    <td style={tdStyle}>{item.PaymentMethod || "-"}</td>
                    <td style={tdStyle}>{item.paymentStatus || "-"}</td>
                    <td style={tdStyle}>{getFullName(item.deliveryBoy)}</td>
                    <td style={tdStyle}>{(wastagePercent + commissionPercent).toFixed(2)}%</td>
                    <td style={tdStyle}> ₹ {(makingChargesPerGram + wastageMakingChargesPerGram).toFixed(2)}</td>
                    <td style={tdStyle}>
                      {item.PaymentMethod?.toUpperCase() === "METAL PAY" && item.finalMetalPay
                        ? `${formatNumberIN(Number(item.finalMetalPay))}`
                        : "-"}
                    </td>
                    <td style={tdStyle}>₹ {buyertotalAmount.toFixed(2)}</td>
                    <td style={tdStyle}>₹ {buyertotalAmmoumtGst.toFixed(2)}</td>
                    <td style={tdStyle}>₹ {discountAmount.toFixed(2)}</td>
                    <td style={tdStyle}>₹ {discountAfterAmount.toFixed(2)}</td>
                    <td style={tdStyle}>₹ {discountAfterAmountWithGst.toFixed(2)}</td>
                    <td style={tdStyle}>
                      <span style={getOrderStatusStyle(item.orderStatus)}>{item.orderStatus || "Pending"}</span>
                    </td>
                    <td style={tdStyle}>
                      <span style={getAdminStatusStyle(item?.deliverySession?.adminStatus)}>
                        {item?.deliverySession?.adminStatus || "-"}
                      </span>
                    </td>
                    <td style={{ ...tdStyle, textAlign: "center" }}>
                      <button onClick={() => openStatusModal(item)} style={viewButtonStyle}>
                        <Eye size={16} /> View
                      </button>
                    </td>
                    <td style={{ ...tdStyle, textAlign: "center" }}>
                      <button
                        onClick={() => {
                          if (item.deliveryBoyId) {
                            toast.warning("Delivery boy already assigned!");
                            return;
                          }
                          setAssignOrder(item);
                          setSelectedDeliveryBoy("");
                          setShowAssignModal(true);
                        }}
                        style={{
                          ...assignButtonStyle,
                          background: item.deliveryBoyId ? "#aaa" : "#4CAF50",
                          cursor: item.deliveryBoyId ? "not-allowed" : "pointer",
                        }}
                        title={item.deliveryBoyId ? "Already Assigned" : "Assign Delivery Boy"}
                        disabled={Boolean(item.deliveryBoyId)}
                      >
                        <Send size={16} /> Assign
                      </button>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={27} style={{ textAlign: "center", padding: "40px" }}>
                  No orders found matching the selected filters
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div
          className="pagination"
          style={{ marginTop: "20px", display: "flex", gap: "10px", justifyContent: "center", alignItems: "center" }}
        >
          <button
            onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
            disabled={currentPage === 1}
            style={paginationButtonStyle(currentPage === 1)}
          >
            Previous
          </button>
          <span style={{ padding: "8px 16px" }}>
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
            disabled={currentPage === totalPages}
            style={paginationButtonStyle(currentPage === totalPages)}
          >
            Next
          </button>
        </div>
      )}

      {/* Invoice Modal */}
      {showModal && selectedOrder && (
        <div className="modal-overlay" style={modalOverlayStyle}>
          <div
            className="modal-box"
            style={{
              width: "1100px",
              maxHeight: "90vh",
              overflowY: "auto",
              backgroundColor: "#0f172a",
              borderRadius: "20px",
              padding: "28px",
              boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.5)",
              border: "1px solid #334155",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "24px",
                borderBottom: "1px solid #334155",
                paddingBottom: "16px",
              }}
            >
              <h3 style={{ margin: 0, fontSize: "1.6rem", fontWeight: "600", color: "#f1f5f9" }}>
                🧾 Order Invoice
              </h3>
              <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
                <span
                  style={{
                    backgroundColor: "#1e293b",
                    padding: "6px 14px",
                    borderRadius: "40px",
                    fontSize: "0.85rem",
                    color: "#94a3b8",
                    fontFamily: "monospace",
                  }}
                >
                  {selectedOrder.orderId}
                </span>
                <span
                  style={{
                    backgroundColor:
                      selectedOrder.orderStatus === "Delivered"
                        ? "#22c55e"
                        : selectedOrder.orderStatus === "On The Way"
                          ? "#f97316"
                          : selectedOrder.orderStatus === "In Progress"
                            ? "#3b82f6"
                            : "#64748b",
                    padding: "6px 14px",
                    borderRadius: "40px",
                    fontSize: "0.75rem",
                    fontWeight: "600",
                    color: "white",
                    textTransform: "uppercase",
                  }}
                >
                  {selectedOrder.orderStatus || "Pending"}
                </span>
              </div>
            </div>

            {/* Product Details */}
            <div
              style={{
                marginBottom: "32px",
                background: "linear-gradient(135deg, #1e293b 0%, #0f172a 100%)",
                borderRadius: "20px",
                padding: "20px",
                border: "1px solid #334155",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "20px" }}>
                <span style={{ fontSize: "24px" }}>📦</span>
                <h4 style={{ margin: 0, color: "#e2e8f0", fontWeight: "500" }}>Product Details</h4>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: "16px" }}>
                <div style={{ color: "#cbd5e1" }}><span style={{ color: "#94a3b8", fontWeight: "500" }}>Category Name :</span> {selectedOrder.category?.name || "-"}</div>
                <div style={{ color: "#cbd5e1" }}><span style={{ color: "#94a3b8", fontWeight: "500" }}>Metal Type:</span> {selectedOrder.metalType?.name || "-"}</div>
                <div style={{ color: "#cbd5e1" }}><span style={{ color: "#94a3b8", fontWeight: "500" }}>Article Name:</span> {selectedOrder.item?.articleName || "-"}</div>
                <div style={{ color: "#cbd5e1" }}><span style={{ color: "#94a3b8", fontWeight: "500" }}>UOM:</span> {selectedOrder.uom?.name || "-"}</div>
                <div style={{ color: "#cbd5e1" }}><span style={{ color: "#94a3b8", fontWeight: "500" }}>Purity:</span> {selectedOrder.purity || "-"}</div>
                <div style={{ color: "#cbd5e1" }}><span style={{ color: "#94a3b8", fontWeight: "500" }}>Current Metal Price :</span> ₹ {perGmPrice.toFixed(2)}</div>
                <div style={{ color: "#cbd5e1" }}><span style={{ color: "#94a3b8", fontWeight: "500" }}>GST %:</span> {gstPercent}%</div>
                <div style={{ color: "#cbd5e1" }}><span style={{ color: "#94a3b8", fontWeight: "500" }}>Purchasing Quantity:</span> {quantity} {selectedOrder.uom?.name || ""}</div>
              </div>
            </div>

            {/* Seller & Buyer Invoices */}
            <div style={{ display: "flex", gap: "24px", marginBottom: "32px", flexWrap: "wrap" }}>
              {/* Seller Invoice */}
              <div
                style={{
                  flex: 1,
                  minWidth: "300px",
                  background: "#1e293b",
                  borderRadius: "20px",
                  overflow: "hidden",
                  border: "1px solid #334155",
                }}
              >
                <div
                  style={{
                    background: "linear-gradient(135deg, #334155, #1e293b)",
                    padding: "14px 20px",
                    color: "#facc15",
                    fontWeight: "600",
                    fontSize: "1.2rem",
                    borderBottom: "1px solid #475569",
                  }}
                >
                  🏪 Seller Invoice
                </div>
                <div style={{ padding: "20px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "12px", borderBottom: "1px dashed #334155", paddingBottom: "6px", color: "#e2e8f0" }}>
                    <span style={{ color: "#94a3b8" }}>Original Tunch to seller:</span>
                    <span style={{ fontWeight: "500" }}>{sellerPurity}</span>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "12px", borderBottom: "1px dashed #334155", paddingBottom: "6px", color: "#e2e8f0" }}>
                    <span style={{ color: "#94a3b8" }}>Quantity:</span>
                    <span style={{ fontWeight: "500" }}>{quantity} {selectedOrder.uom?.name || ""}</span>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "12px", borderBottom: "1px dashed #334155", paddingBottom: "6px", color: "#e2e8f0" }}>
                    <span style={{ color: "#94a3b8" }}>Fine:</span>
                    <span style={{ fontWeight: "500" }}>{formatNumberIN(sellerFineGm)} gm</span>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "12px", borderBottom: "1px dashed #334155", paddingBottom: "6px", color: "#e2e8f0" }}>
                    <span style={{ color: "#94a3b8" }}>Price (without making charges):</span>
                    <span style={{ fontWeight: "500" }}>₹ {sellerPriceWithoutMaking.toFixed(2)}</span>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      marginBottom: "12px",
                      borderBottom: "1px dashed #334155",
                      paddingBottom: "6px",
                      color: "#e2e8f0",
                    }}
                  >
                    <span style={{ color: "#94a3b8" }}>
                      Making Charges ({makingChargesPerGram.toFixed(2)} per gm)
                    </span>
                    <span style={{ fontWeight: "500" }}>
                      ₹ {(makingChargesPerGram * quantity).toFixed(2)}
                    </span>
                  </div>
                  <div style={{ marginTop: "16px", paddingTop: "12px", borderTop: "1px solid #475569", fontWeight: "bold", display: "flex", justifyContent: "space-between", color: "#facc15" }}>
                    <span>Subtotal:</span>
                    <span>₹ {sellerTotalAmount.toFixed(2)}</span>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", marginTop: "6px", color: "#cbd5e1" }}>
                    <span>GST ({gstPercent}%):</span>
                    <span>₹ {((sellerTotalAmount * gstPercent) / 100).toFixed(2)}</span>
                  </div>
                  <div
                    style={{
                      marginTop: "12px",
                      paddingTop: "12px",
                      borderTop: "2px solid #facc15",
                      fontWeight: "bold",
                      fontSize: "1.1rem",
                      display: "flex",
                      justifyContent: "space-between",
                      color: "white",
                    }}
                  >
                    <span>Grand Total:</span>
                    <span>₹ {(sellerTotalAmount + (sellerTotalAmount * gstPercent) / 100).toFixed(2)}</span>
                  </div>
                </div>
              </div>

              {/* Buyer Invoice */}
              <div
                style={{
                  flex: 1,
                  minWidth: "300px",
                  background: "#1e293b",
                  borderRadius: "20px",
                  overflow: "hidden",
                  border: "1px solid #334155",
                }}
              >
                <div
                  style={{
                    background: "linear-gradient(135deg, #334155, #1e293b)",
                    padding: "14px 20px",
                    color: "#34d399",
                    fontWeight: "600",
                    fontSize: "1.2rem",
                    borderBottom: "1px solid #475569",
                  }}
                >
                  💰 Buyer Invoice
                </div>
                <div style={{ padding: "20px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "12px", borderBottom: "1px dashed #334155", paddingBottom: "6px", color: "#e2e8f0" }}>
                    <span style={{ color: "#94a3b8" }}>Original Tunch to buyer:</span>
                    <span style={{ fontWeight: "500" }}>{buyerPurity}</span>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "12px", borderBottom: "1px dashed #334155", paddingBottom: "6px", color: "#e2e8f0" }}>
                    <span style={{ color: "#94a3b8" }}>Purchasing Quantity:</span>
                    <span style={{ fontWeight: "500" }}>{quantity} {selectedOrder.uom?.name || ""}</span>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "12px", borderBottom: "1px dashed #334155", paddingBottom: "6px", color: "#e2e8f0" }}>
                    <span style={{ color: "#94a3b8" }}>Fine:</span>
                    <span style={{ fontWeight: "500" }}>{formatNumberIN(buyerFineGm)} gm</span>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "12px", borderBottom: "1px dashed #334155", paddingBottom: "6px", color: "#e2e8f0" }}>
                    <span style={{ color: "#94a3b8" }}>Price (without making charges):</span>
                    <span style={{ fontWeight: "500" }}>₹ {buyerPriceWithoutMaking.toFixed(2)}</span>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      marginBottom: "12px",
                      borderBottom: "1px dashed #334155",
                      paddingBottom: "6px",
                      color: "#e2e8f0",
                    }}
                  >
                    <span style={{ color: "#94a3b8" }}>
                      Making Charges ({makingChargesPerGram.toFixed(2)} per gm)
                    </span>
                    <span style={{ fontWeight: "500" }}>
                      ₹ {(makingChargesPerGram * quantity).toFixed(2)}
                    </span>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "12px", borderBottom: "1px dashed #334155", paddingBottom: "6px", color: "#e2e8f0" }}>
                    <span style={{ color: "#94a3b8" }}>Margin Making Charges:</span>
                    <span style={{ fontWeight: "500" }}>₹ {marginMakingChargesCorrected.toFixed(2)}</span>
                  </div>

                  <div style={{ marginTop: "16px", paddingTop: "12px", borderTop: "1px solid #475569", fontWeight: "bold", display: "flex", justifyContent: "space-between", color: "#34d399" }}>
                    <span>Subtotal:</span>
                    <span>₹ {buyerTotalAmount.toFixed(2)}</span>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", marginTop: "6px", color: "#cbd5e1" }}>
                    <span>GST ({gstPercent}%):</span>
                    <span>₹ {((buyerTotalAmount * gstPercent) / 100).toFixed(2)}</span>
                  </div>
                  <div
                    style={{
                      marginTop: "12px",
                      paddingTop: "12px",
                      borderTop: "2px solid #34d399",
                      fontWeight: "bold",
                      fontSize: "1.1rem",
                      display: "flex",
                      justifyContent: "space-between",
                      color: "white",
                    }}
                  >
                    <span>Grand Total:</span>
                    <span>₹ {buyerTotalAmountGst.toFixed(2)}</span>
                  </div>

                  {/* ✅ DISCOUNT SECTION - AFTER GRAND TOTAL */}
                  {discountAmount > 0 && (
                    <div style={{ marginTop: "16px", paddingTop: "16px", borderTop: "2px solid #facc15" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "8px", color: "#fbbf24" }}>
                        <span style={{ color: "#fcd34d", fontWeight: "500" }}>💳 Discount Amount:</span>
                        <span style={{ fontWeight: "600", color: "#fbbf24" }}>- ₹ {discountAmount.toFixed(2)}</span>
                      </div>
                      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "8px", color: "#fbbf24" }}>
                        <span style={{ color: "#fcd34d", fontWeight: "500" }}>📉 Final Discount Amount:</span>
                        <span style={{ fontWeight: "600", color: "#fbbf24" }}>₹ {discountAfterAmount.toFixed(2)}</span>
                      </div>
                      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "8px", color: "#fbbf24" }}>
                        <span style={{ color: "#fcd34d", fontWeight: "500" }}>💰 Discount Amount + GST:</span>
                        <span style={{ fontWeight: "600", color: "#fbbf24" }}>₹ {discountAfterAmountWithGst.toFixed(2)}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Admin Profit - ONLY visible for Super Admin */}
            {isSuperAdmin && (
              <div
                style={{
                  marginBottom: "32px",
                  background: "linear-gradient(135deg, #2d2a1e, #1e1b12)",
                  borderRadius: "20px",
                  padding: "20px",
                  border: "1px solid #facc15",
                  boxShadow: "0 4px 14px 0 rgba(250, 204, 21, 0.1)",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "16px" }}>
                  <span style={{ fontSize: "22px" }}>📊</span>
                  <h4 style={{ margin: 0, color: "#facc15", fontWeight: "500" }}>Admin Profit</h4>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: "20px", color: "#fef3c7" }}>
                  <div><span style={{ color: "#fcd34d", fontWeight: "500" }}>Margin Wastage Profit:</span> <strong>₹ {marginWastageProfit.toFixed(2)}</strong></div>
                  <div><span style={{ color: "#fcd34d", fontWeight: "500" }}>Margin Making Charges Profit:</span> <strong>₹ {marginMakingChargesCorrected.toFixed(2)}</strong></div>
                  <div><span style={{ color: "#fcd34d", fontWeight: "500" }}>Total Admin Profit:</span> <strong>₹ {(marginWastageProfit + marginMakingChargesCorrected).toFixed(2)}</strong></div>
                  <div><span style={{ color: "#fcd34d", fontWeight: "500" }}>Admin Fine:</span> <strong>{formatNumberIN(adminFineGm)} gm</strong></div>
                </div>
              </div>
            )}

            {/* Additional Info Row */}
            <div
              style={{
                background: "#1e293b",
                borderRadius: "16px",
                padding: "16px 20px",
                marginBottom: "32px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: "20px",
                border: "1px solid #334155",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                <span style={{ fontWeight: "500", color: "#cbd5e1" }}>👁️ Price Visibility:</span>
                <select
                  value={selectedOrder.priceVisibility === 1 ? "Yes" : "No"}
                  onChange={async (e) => {
                    const value = e.target.value === "Yes" ? 1 : 0;
                    try {
                      await updatePriceVisibility(selectedOrder.orderId, { priceVisibility: value });
                      setSelectedOrder((prev: any) => ({ ...prev, priceVisibility: value }));
                      setOrders((prev) =>
                        prev.map((order) =>
                          order.orderId === selectedOrder.orderId ? { ...order, priceVisibility: value } : order
                        )
                      );
                      toast.success("Price visibility updated");
                    } catch (err) {
                      toast.error("Update failed");
                    }
                  }}
                  style={{
                    padding: "6px 12px",
                    borderRadius: "40px",
                    background: "#0f172a",
                    color: "#e2e8f0",
                    border: "1px solid #475569",
                    fontWeight: "500",
                    cursor: "pointer",
                  }}
                >
                  <option value="Yes">Yes</option>
                  <option value="No">No</option>
                </select>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                <span style={{ fontWeight: "500", color: "#cbd5e1" }}>📸 Pickup Image:</span>
                {selectedOrder?.deliverySession?.pickupImage ? (
                  <img
                    src={selectedOrder.deliverySession.pickupImage}
                    alt="pickup"
                    style={{ width: "70px", height: "70px", objectFit: "cover", borderRadius: "12px", cursor: "pointer", border: "1px solid #475569", transition: "transform 0.2s" }}
                    onClick={() => window.open(selectedOrder.deliverySession.pickupImage, "_blank")}
                    onMouseEnter={(e) => (e.currentTarget.style.transform = "scale(1.05)")}
                    onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
                  />
                ) : <span style={{ color: "#94a3b8" }}>No Image</span>}
              </div>

              <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                <span style={{ fontWeight: "500", color: "#cbd5e1" }}>🚚 Payment Image:</span>
                {selectedOrder?.deliverySession?.deliveryImage ? (
                  <img
                    src={selectedOrder.deliverySession.deliveryImage}
                    alt="delivery"
                    style={{ width: "70px", height: "70px", objectFit: "cover", borderRadius: "12px", cursor: "pointer", border: "1px solid #475569", transition: "transform 0.2s" }}
                    onClick={() => window.open(selectedOrder.deliverySession.deliveryImage, "_blank")}
                    onMouseEnter={(e) => (e.currentTarget.style.transform = "scale(1.05)")}
                    onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
                  />
                ) : <span style={{ color: "#94a3b8" }}>No Image</span>}
              </div>
            </div>

            {/* Status & Delivery Control */}
            <div style={{ display: "flex", gap: "24px", flexWrap: "wrap", borderTop: "1px solid #334155", paddingTop: "24px" }}>
              <div style={{ flex: 1, background: "#1e293b", borderRadius: "16px", padding: "18px", border: "1px solid #334155" }}>
                <h4 style={{ margin: "0 0 16px 0", color: "#e2e8f0", display: "flex", gap: "8px" }}>🚚 Delivery Control</h4>
                <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
                  <select disabled={isAlreadyOutForDelivery} value={adminStatus} onChange={(e) => setAdminStatus(e.target.value as AdminStatusState)} style={{ flex: 1, padding: "10px 14px", borderRadius: "12px", background: isAlreadyOutForDelivery ? "#334155" : "#0f172a", color: "#f1f5f9", border: "1px solid #475569", cursor: isAlreadyOutForDelivery ? "not-allowed" : "pointer", fontWeight: "500" }}>
                    <option value="">Select Admin Status</option>
                    {adminStatusOptions.map((status) => (<option key={status} value={status}>{status}</option>))}
                  </select>
                  <button disabled={isDeliveryUpdateDisabled} style={{ background: isDeliveryUpdateDisabled ? "#475569" : "#f97316", color: "#fff", padding: "10px 18px", border: "none", borderRadius: "40px", cursor: isDeliveryUpdateDisabled ? "not-allowed" : "pointer", fontWeight: "600", transition: "0.2s" }} onClick={async () => {
                    if (!isPickupImageAvailable) { toast.warning("Pickup image is required"); return; }
                    if (isAlreadyOutForDelivery) { toast.warning("Already marked as Go for Delivery"); return; }
                    if (!adminStatus) { toast.warning("Select admin status"); return; }
                    try {
                      await updateAdminStatusForDelivery(selectedOrder.orderId, { adminStatus });
                      setSelectedOrder((prev: any) => ({ ...prev, deliverySession: { ...prev?.deliverySession, adminStatus } }));
                      setOrders((prev) => prev.map((order) => order.orderId === selectedOrder.orderId ? { ...order, deliverySession: { ...order.deliverySession, adminStatus } } : order));
                      toast.success(`Delivery status updated to ${adminStatus}`);
                      fetchOrders(false);
                    } catch (err) { toast.error("Update failed"); }
                  }}>Update 🚚</button>
                  <button style={{ ...secondaryButtonStyle, background: "#475569", borderRadius: "40px", padding: "8px 20px" }} onClick={() => setShowModal(false)}>Cancel</button>
                </div>
                {!isPickupImageAvailable && <div style={{ fontSize: "12px", color: "#facc15", marginTop: "10px" }}>⚠️ Pickup image required before delivery update</div>}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Assign Delivery Boy Modal */}
      {showAssignModal && (
        <div className="modal-overlay" style={modalOverlayStyle}>
          <div className="modal-box assign-modal" style={{ backgroundColor: "white", borderRadius: "8px", padding: "20px", width: "400px" }}>
            <h3>🚚 Assign Delivery Boy</h3>
            <select value={selectedDeliveryBoy} onChange={(e) => setSelectedDeliveryBoy(e.target.value)} style={{ width: "100%", padding: "8px", marginBottom: "15px" }}>
              <option value="">Select Delivery Boy</option>
              {deliveryBoys.map((boy) => (<option key={boy.id} value={boy.id}>{boy.firstName} {boy.lastName}</option>))}
            </select>
            <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
              <button style={primaryButtonStyle} onClick={handleAssignDeliveryBoy}>Assign</button>
              <button style={secondaryButtonStyle} onClick={() => setShowAssignModal(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderList;